'use client';

import { ArrowLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';

import { toIsoDay } from '@/lib/seo/iso-day';
import { Button } from '@naumu/ui/components/button';
import { NativeOverscroll } from '@naumu/ui/components/native-overscroll';
import { cn } from '@naumu/ui/utils/cn';

import type { LegalDocumentType } from '../helpers/legal-content';
import { LEGAL_DOCUMENTS } from '../helpers/legal-content';

interface LegalPageLayoutProps {
	documentType: LegalDocumentType;
	className?: string;
}

export const LegalPageLayout = ({
	documentType,
	className
}: LegalPageLayoutProps) => {
	const router = useRouter();
	const document = LEGAL_DOCUMENTS[documentType];
	// "June 2, 2026" -> "2026-06-02" for the machine-readable <time>. This is a
	// client component, so the day must not depend on the renderer's timezone -
	// toIsoDay does no Date math (see lib/seo/iso-day.ts).
	const lastUpdatedIso = toIsoDay(document.lastUpdated);

	const handleBack = () => {
		if (window.history.length > 1) {
			router.back();
		} else {
			router.push('/');
		}
	};

	// Legal pages sit outside the (marketing) shell by design, so this layout has
	// to supply the page's own <main> landmark - without it these routes ship no
	// landmark at all.
	return (
		<div
			className={cn(
				'h-screen overflow-y-auto bg-background text-neutral',
				className
			)}>
			<NativeOverscroll />
			<main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
				{/* Back Button */}
				<Button
					variant="ghost-muted"
					size="sm"
					onClick={handleBack}
					className="mb-8">
					<ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
					Back
				</Button>

				<article className="prose prose-neutral dark:prose-invert max-w-none">
					{/* Header */}
					<header className="mb-10">
						<h1 className="text-3xl font-semibold text-neutral mb-2">
							{document.title}
						</h1>
						<p className="text-sm text-neutral-muted">
							Last updated:{' '}
							<time dateTime={lastUpdatedIso}>{document.lastUpdated}</time>
						</p>
					</header>

					{/* Content */}
					<div className="leading-relaxed font-light text-neutral/90">
						{document.content}
					</div>
				</article>
			</main>
		</div>
	);
};
